import { useEffect, useState } from 'react';
import { Route, Routes, useParams } from 'react-router';

import { Button, Header, ReadReceiptBadge, Spinner } from '../../common/components';
import routes from '../../common/constants/routes';
import { useAppSelector, useAppThunkDispatch, useDocumentTitle } from '../../common/hooks';
import { t } from '../../common/i18n';
import { getPoll, selectPoll } from '../../common/store/pollSlice';

import { TabPoll } from './TabPoll';
import { TabVoterList } from './TabVoterList';
import { TabDetail } from './TabDetail';
import { generatePollTicket, generateResults } from '../services/PollService';
import { selectSecurity } from '../../common/store/securitySlice';
import { PollMinutesModal } from './PollMinutesModal';

export const PollResults: React.FC = () => {
  useDocumentTitle(t('Poll.title'));

  const { pollId = null } = useParams();
  const dispatch = useAppThunkDispatch();
  const poll = useAppSelector(selectPoll);
  const ui = useAppSelector(selectSecurity).ui;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isMinutesModalShown, setIsMinutesModalShown] = useState<boolean>(false);

  const isAdmin = ui.state === 'admin' ? true : undefined;
  // Lístky jde na API vygenerovat jen pro aktivní hlasování (canBeVotedInPollAsMember) — po uzavření tlačítko skrýváme.
  const canGenerateTickets = isAdmin && poll.state === 'active';
  const canGenerateResults = isAdmin && poll.isResultPublished;

  const handleGenerateTicket = () => generatePollTicket(poll.id!);
  const handleGenerateResults = () => generateResults(poll.id!);

  useEffect(() => {
    if (pollId === null) return;
    dispatch(getPoll(pollId))
      .unwrap()
      .then(() => setIsLoading(false));
  }, [pollId, dispatch]);

  if (isLoading) return <Spinner size="large" position="overlay" />;

  const basePath = `${routes.poll}results/${pollId}`;

  return (
    <>
      <Header
        title={poll.name || ''}
        customBacklink={`${routes.poll}`}
        secondRow={
          isAdmin && poll.id && !poll.isDraft ? <ReadReceiptBadge subjectType="poll" subjectId={poll.id} /> : undefined
        }
        tabs={[
          { label: t('Poll.Results.TabPoll.tabTitle'), to: basePath },
          { label: t('Poll.Results.TabVoterList.tabTitle'), to: `${basePath}/voter-list`, renderCondition: 'isAdmin' },
          { label: t('Poll.Results.TabDetail.tabTitle'), to: `${basePath}/detail` },
        ]}
        actions={
          <>
            {canGenerateTickets && (
              <Button
                size="small"
                iconBefore="printer"
                title={t('Poll.Results.TabVoterList.Ticket.generateAll')}
                onClick={handleGenerateTicket}
              />
            )}
            {canGenerateResults && (
              <Button
                size="small"
                iconBefore="document"
                title={t('Poll.Results.Minutes.button')}
                onClick={() => setIsMinutesModalShown(true)}
              />
            )}
            {canGenerateResults && (
              <Button
                size="small"
                iconBefore="fileXlsx"
                title={t('Poll.Results.TabVoterList.Ticket.generateResults')}
                onClick={handleGenerateResults}
              />
            )}
          </>
        }
      />

      {canGenerateResults && poll.id && (
        <PollMinutesModal
          pollId={poll.id}
          isShown={isMinutesModalShown}
          onHide={() => setIsMinutesModalShown(false)}
        />
      )}

      <div className="Content">
        <Routes>
          <Route index element={<TabPoll />} />
          <Route path="voter-list" element={<TabVoterList />} />
          <Route path="detail" element={<TabDetail />} />
        </Routes>
      </div>
    </>
  );
};

export default PollResults;
